import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';

interface ProtectedRouteProps {
  children: ReactNode;
}

/**
 * Route guard that redirects unauthenticated visitors to /login while
 * remembering where they were trying to go. Auth state is read from the
 * zustand store; the /login flow sets it and navigates back to the saved
 * path on success.
 *
 * If the server marked the user as password_reset_required (e.g. after a
 * tenant admin seeded the account with a temporary password), every
 * route except /account/change-password redirects back to the change
 * page until the user completes the reset.
 */
export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const tokens = useAuthStore((s) => s.tokens);
  const user = useAuthStore((s) => s.user);
  const location = useLocation();

  if (!tokens) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  if (user?.passwordResetRequired && location.pathname !== '/account/change-password') {
    return <Navigate to="/account/change-password" replace />;
  }

  return <>{children}</>;
}
